import { articles } from './articles'

export interface ArticleLink {
  title: string
  url: string
}

export interface KnowledgeEntry {
  keywords: string[]
  response: string
  relatedArticle: ArticleLink
  followUp?: string[]
}

export const chatKnowledge: KnowledgeEntry[] = [
  {
    keywords: ['focus', 'concentrat', 'distract', '12 hour'],
    response:
      "Focus is a skill, not a trait. The Productivity Blueprint recommends the 'Rolling Time-Block' method: instead of rigid schedules, define 3 core priorities and work on them in 90-minute sprints with 15-minute breaks. If you're struggling right now, try the '5-Minute Rule' — commit to just 5 minutes of the task. Usually, the friction is just in starting.",
    relatedArticle: {
      title: articles[1].title,
      url: articles[1].url,
    },
    followUp: [
      'Tell me more about time-blocking',
      'How do I handle interruptions?',
    ],
  },
  {
    keywords: ['morning', 'routine', 'start', 'wake'],
    response:
      "A great morning starts the night before. Our 'Shutdown Ritual' (writing down tomorrow's 3 priorities) is key. For the morning itself, we recommend 'The First Hour Principle': No phone, no email — just deep work on your #1 priority for the first 60 minutes of your day. And if you can, add a walk. The benefits compound fast.",
    relatedArticle: {
      title: articles[5].title,
      url: articles[5].url,
    },
    followUp: ['What is a Shutdown Ritual?', 'I struggle to wake up early'],
  },
  {
    keywords: ['tired', 'burnout', 'exhaust', 'energy', 'rest'],
    response:
      "Productivity isn't about doing more — it's about managing energy. If you're feeling burnt out, you might be ignoring your 'Ultradian Rhythms'. Your brain can only focus intensely for about 90 minutes before needing a reset. Try taking a 'NSDR' (Non-Sleep Deep Rest) break or a 10-minute walk without your phone.",
    relatedArticle: {
      title: articles[1].title,
      url: articles[1].url,
    },
    followUp: ['What is NSDR?', 'How do I rest effectively?'],
  },
  {
    keywords: [
      'procrastinat',
      'lazy',
      'motivation',
      'stuck',
      'giving up',
      'give up',
    ],
    response:
      "Procrastination is usually an emotional regulation problem, not a time management one. We avoid tasks that make us feel anxious or incompetent. Try 'The One Question' reframe: 'What is the very next physical action I need to take?' Make it so small it feels ridiculous to say no to. This single shift has helped thousands of readers get unstuck.",
    relatedArticle: {
      title: articles[0].title,
      url: articles[0].url,
    },
    followUp: ['Give me an example', 'I feel overwhelmed'],
  },
  {
    keywords: ['tool', 'app', 'system', 'hammer', 'screw', 'software'],
    response:
      "Tools matter, but systems matter more. As we wrote in 'To Hammer A Screw', using the wrong tool for the job creates unnecessary friction. Don't use a complex project management tool for a simple grocery list. Match the complexity of the tool to the complexity of the problem. Start with pen and paper, then upgrade only when you hit a real limitation.",
    relatedArticle: {
      title: articles[4].title,
      url: articles[4].url,
    },
    followUp: ['What tools do you recommend?', 'How do I organize my tasks?'],
  },
  {
    keywords: [
      'plan',
      'schedule',
      'calendar',
      'week',
      'time-block',
      'time block',
      'timeblock',
    ],
    response:
      "We recommend a 'Weekly Review' every Sunday. Look back at what you accomplished, clear your physical and digital inboxes, and plan your 'Big 3' outcomes for the coming week. For daily execution, use the Rolling Time-Block method: assign your top 3 priorities to 90-minute blocks, but stay flexible — move blocks around as energy and context shift.",
    relatedArticle: {
      title: articles[2].title,
      url: articles[2].url,
    },
    followUp: ['How do I do a weekly review?', 'What are Big 3 outcomes?'],
  },
  {
    keywords: ['attention', 'wander', 'mind wander', 'refocus', 'scatter'],
    response:
      "Your mind will wander — that's completely normal. The key is having a system to bring it back. Some quick wins: (1) Write down the distraction and return to your task, (2) Use the 'one breath reset' — take a single deep breath and re-read your last sentence, (3) Change your physical position. We covered 15 practical techniques in a recent deep dive.",
    relatedArticle: {
      title: articles[3].title,
      url: articles[3].url,
    },
    followUp: [
      'Give me more refocusing tips',
      'Why does my mind wander so much?',
    ],
  },
  {
    keywords: ['habit', 'build', 'consistency', 'stick', 'routine'],
    response:
      "The secret to habits isn't motivation — it's systems. Start with the '2-Minute Rule': scale any habit down to something you can do in 2 minutes. Want to read more? Start with one page. Want to exercise? Start with putting on your shoes. Stack new habits onto existing ones (after I pour my coffee, I'll write for 2 minutes). Consistency beats intensity every time.",
    relatedArticle: {
      title: articles[5].title,
      url: articles[5].url,
    },
    followUp: ['What is habit stacking?', 'How long until a habit sticks?'],
  },
  {
    keywords: [
      'overwhelm',
      'too much',
      'busy',
      'everything',
      'urgent',
      'priorit',
    ],
    response:
      "When everything feels urgent, nothing gets done. The fix is 'Self Alignment' — getting clear on what actually matters to you, not just what's loudest. Try this: write down everything on your plate, then ask 'If I could only do ONE of these today, which would move the needle most?' Do that first. Let the rest wait.",
    relatedArticle: {
      title: articles[2].title,
      url: articles[2].url,
    },
    followUp: ['How do I say no to things?', 'Help me prioritize my tasks'],
  },
  {
    keywords: ['walk', 'exercise', 'health', 'move', 'physical'],
    response:
      'Movement is one of the most underrated productivity tools. A daily morning walk — even just 10 minutes — can reset your nervous system, boost creative thinking, and set a proactive tone for the day. No podcast, no phone. Just walk and let your mind process. The compounding benefits are remarkable.',
    relatedArticle: {
      title: articles[5].title,
      url: articles[5].url,
    },
    followUp: ['How long should I walk?', 'What about exercise and focus?'],
  },
  {
    keywords: ['hello', 'hi', 'hey', 'greeting', 'help'],
    response:
      "Hey there! 👋 I'm your AI productivity guide, built on insights from The Productivity Blueprint newsletter. I can help with focus strategies, morning routines, beating procrastination, building habits, and more. What are you working on today?",
    relatedArticle: {
      title: articles[0].title,
      url: articles[0].url,
    },
    followUp: [
      'I need to focus better',
      'I want to build a habit',
      'I feel overwhelmed',
    ],
  },
  {
    keywords: ['deep work', 'flow', 'zone', 'productive session'],
    response:
      "Deep work requires three things: a clear target, a distraction-free environment, and a time boundary. Set a 90-minute block, silence all notifications, and define exactly what 'done' looks like before you start. The first 15 minutes will feel hard — that's normal. Push through and you'll hit your stride.",
    relatedArticle: {
      title: articles[1].title,
      url: articles[1].url,
    },
    followUp: [
      'How do I eliminate distractions?',
      'What if I get interrupted?',
    ],
  },
  {
    keywords: ['goal', 'purpose', 'direction', 'align', 'meaning'],
    response:
      "Goals without alignment are just busy work. Before setting targets, ask yourself: 'Does this move me toward the person I want to become?' Self-alignment means your daily actions reflect your deeper values — not just your inbox. Start with a 'North Star' question: What would make this week feel meaningful?",
    relatedArticle: {
      title: articles[2].title,
      url: articles[2].url,
    },
    followUp: ['How do I find my purpose?', 'Help me set better goals'],
  },
]

export const defaultResponse =
  "That's a great question! While I don't have a specific framework for that yet, The Productivity Blueprint explores new productivity science every week. Subscribe to get fresh insights every Sunday!"

export const defaultArticle: ArticleLink = {
  title: 'Browse the full archive',
  url: 'https://theproductivityblueprint.beehiiv.com/archive',
}
